import { Modal, Flex, Button, Text, ActionIcon, Title, Divider } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	ACCEPT_FILES_TYPES,
	DropzoneV2,
} from '@dealstack/frontend/shared/components/DropzoneV2/DropzoneV2';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	useTranslationInvestor,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { SelectedFileBox } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SelectedFileBox';
import { Circle } from '@dealstack/frontend/shared/components/common/Circle';

interface Props {
	opened: boolean;
	onClose: () => void;
	onDownloadContract: () => void;
	onSave: () => void;
	isSaving: boolean;
	selectedFile: File | undefined;
	onFileSelect: (file: File) => void;
	onFileRemove: () => void;
	isPrefillable: boolean;
}

export const WetInkSigningModal = ({
	opened,
	onClose,
	onDownloadContract,
	onSave,
	isSaving,
	selectedFile,
	onFileSelect,
	onFileRemove,
	isPrefillable,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tInvestor = useTranslationInvestor();

	const isSaveDisabled = !selectedFile || isSaving;

	return (
		<Modal
			title={tMipManage('documents_page.counterparty_signing_modal.title')}
			opened={opened}
			onClose={onClose}
			size="lg"
			centered
		>
			{isSaving && <InlineLoader />}
			<Flex direction="column" gap="md">
				<Text size="sm" c="dimmed">
					{tMipManage('documents_page.counterparty_signing_modal.description')}
				</Text>

				<Button
					variant="outline"
					leftSection={<Icon variant="download" size="md" />}
					onClick={onDownloadContract}
				>
					{tMipManage('documents_page.counterparty_signing_modal.download_contract')}
				</Button>

				{isPrefillable && (
					<>
						<Flex align="baseline" gap="0.5rem">
							<Circle variant="filled">
								<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
									1
								</Text>
							</Circle>

							<Flex direction="column" gap="0.5rem">
								<Title order={5}>
									{tInvestor('documents.submittable_modal.download_print')}
								</Title>
								<Text sx={{ maxWidth: '20rem' }}>
									{tInvestor('documents.submittable_modal.download_print_descr')}
								</Text>
							</Flex>
						</Flex>
						<Divider />
						<Flex align="baseline" gap="0.5rem">
							<Circle variant="filled">
								<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
									2
								</Text>
							</Circle>
							<Flex direction="column" gap="0.5rem">
								<Title order={5}>
									{tInvestor('documents.submittable_modal.complete_it')}
								</Title>
								<Text sx={{ maxWidth: '20rem' }}>
									{tInvestor('documents.submittable_modal.complete_doc')}
								</Text>
							</Flex>
						</Flex>
						<Divider />

						<Flex align="baseline" gap="0.5rem">
							<Circle variant="filled">
								<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
									3
								</Text>
							</Circle>
							<Flex direction="column" gap="0.5rem">
								<Title order={5}>
									{tInvestor('documents.submittable_modal.scan_upload')}
								</Title>
								<Text sx={{ maxWidth: '20rem' }}>
									{tInvestor('documents.submittable_modal.scan_upload_descr')}
								</Text>
							</Flex>
						</Flex>
					</>
				)}

				{selectedFile ? (
					<SelectedFileBox>
						<Flex justify="space-between" align="center">
							<Flex gap="xs" align="center">
								<Icon variant="document-v3" size="lg" />
								<Text size="sm" fw={500}>
									{selectedFile.name}
								</Text>
							</Flex>
							<ActionIcon
								variant="subtle"
								color="red"
								onClick={onFileRemove}
								aria-label="Remove file"
							>
								<Icon variant="trash-can" size="md" />
							</ActionIcon>
						</Flex>
					</SelectedFileBox>
				) : (
					<DropzoneV2
						onDropFile={onFileSelect}
						accept={ACCEPT_FILES_TYPES.pdf}
						title={tMipManage(
							'documents_page.counterparty_signing_modal.dropzone_text'
						)}
						description={tMipManage(
							'documents_page.counterparty_signing_modal.dropzone_hint'
						)}
						buttonText={tMipManage(
							'documents_page.counterparty_signing_modal.dropzone_button_text'
						)}
					/>
				)}

				<Flex justify="flex-end" gap="sm">
					<Button variant="outline" onClick={onClose}>
						{tMipManage('documents_page.counterparty_signing_modal.cancel')}
					</Button>
					<Button
						onClick={onSave}
						disabled={isSaveDisabled}
						leftSection={<Icon variant="checkmark-circle" size="md" />}
					>
						{tMipManage('documents_page.counterparty_signing_modal.save')}
					</Button>
				</Flex>
			</Flex>
		</Modal>
	);
};
